// frontend/src/components/landing/sections/ProductShowcase.js
import React from 'react';
import { Box, Typography } from '@mui/material';
import { Container, Reveal, staggerItem } from '../parts';
import useReveal from '../useReveal';
import { tokens } from '../landingTheme';

// Made-up utterances (no real transcripts in the repo or on the page)
const EXCERPT = [
    { speaker: 'Teacher', text: 'What did you notice when we dropped the ice into the oil?', ut: 'Q', rc: 'OD' },
    { speaker: 'Student A', text: 'It stayed on top for a while.', ut: 'Rs', rc: 'OD' },
    { speaker: 'Student B', text: 'I think because the ice is less dense than the oil.', ut: 'Rs', rc: 'INF', highlight: true },
];

// Illustrative values, not from any lesson
const SUMMARY = [
    { label: 'Everyday reasoning', value: 34, color: '#B9C8DC' },
    { label: 'Scientific knowledge', value: 18, color: tokens.accentMid },
    { label: 'Observation / data', value: 29, color: tokens.accentMid },
    { label: 'Inference from data', value: 19, color: tokens.accentDeep },
];

// Frosted glass only where the cards sit over the photo (md and up); solid when stacked below it
const cardSx = {
    borderRadius: 6,
    p: { xs: 2.5, md: 3 },
    bgcolor: { xs: tokens.surface, md: 'rgba(255, 255, 255, 0.86)' },
    backdropFilter: { md: 'blur(18px) saturate(140%)' },
    border: { xs: `1px solid ${tokens.line}`, md: '1px solid rgba(255, 255, 255, 0.7)' },
    boxShadow: tokens.shadowLift,
};

const CodeTag = ({ children, tone, sx }) => (
    <Box
        component="span"
        sx={{
            ...sx,
            minWidth: 34,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            textAlign: 'center',
            fontSize: '0.75rem',
            fontWeight: 600,
            ...(tone === 'rc'
                ? { bgcolor: tokens.accentTint, color: tokens.accent }
                : { color: tokens.ink, boxShadow: `inset 0 0 0 1px ${tokens.line}` }),
        }}
    >
        {children}
    </Box>
);

const LabelledExcerptCard = () => {
    const [ref, shown] = useReveal();
    return (
        <Box sx={cardSx} ref={ref}>
            <Typography variant="overline" component="h2" sx={{ color: tokens.inkMuted, display: 'block', mb: 1.5 }}>
                Labelled by ADAS
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 }}>
                {EXCERPT.map((row, i) => (
                    <Box
                        component="li"
                        key={row.speaker}
                        sx={{
                            ...staggerItem(shown, 300 + i * 250),
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr auto', sm: '84px 1fr auto' },
                            alignItems: 'center',
                            columnGap: 2,
                            rowGap: 0.5,
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 3,
                            bgcolor: row.highlight ? tokens.highlightTint : 'transparent',
                            boxShadow: row.highlight ? `inset 0 0 0 1px ${tokens.highlight}` : 'none',
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, gridColumn: { xs: '1 / -1', sm: 'auto' } }}>
                            {row.speaker}
                        </Typography>
                        <Typography variant="body2" sx={{ color: tokens.ink }}>
                            {row.text}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                            <CodeTag sx={staggerItem(shown, 550 + i * 250, 'scale(0.4)')}>{row.ut}</CodeTag>
                            <CodeTag tone="rc" sx={staggerItem(shown, 650 + i * 250, 'scale(0.4)')}>{row.rc}</CodeTag>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const LessonSummaryCard = () => {
    const [ref, shown] = useReveal();
    return (
        <Box sx={cardSx} ref={ref}>
            <Typography variant="overline" component="p" sx={{ color: tokens.inkMuted, display: 'block' }}>
                Lesson summary
            </Typography>
            <Typography variant="h3" component="h2" sx={{ fontSize: '1.25rem', mt: 0.5, mb: 2 }}>
                Reasoning in this lesson
            </Typography>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.5 }}>
                {SUMMARY.map((row, i) => (
                    <Box component="li" key={row.label}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography component="span" variant="body2">{row.label}</Typography>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                {row.value}%
                            </Typography>
                        </Box>
                        <Box aria-hidden="true" sx={{ height: 6, borderRadius: 999, bgcolor: tokens.subtle, overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    height: '100%',
                                    borderRadius: 999,
                                    bgcolor: row.color,
                                    width: shown ? `${row.value}%` : 0,
                                    transition: `width 900ms ${tokens.ease} ${200 + i * 100}ms`,
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none', width: `${row.value}%` },
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Box>
            <Typography variant="body2" sx={{ mt: 2, color: tokens.inkMuted, fontStyle: 'italic', fontSize: '0.8125rem' }}>
                Illustrative values
            </Typography>
        </Box>
    );
};

const ProductShowcase = () => (
    <Box component="section" aria-label="An example of ADAS labels and a lesson summary" sx={{ pb: { xs: 10, md: 18 } }}>
        <Container>
            <Box sx={{ position: 'relative' }}>
                <Box
                    component="img"
                    src="/landing/classroom-1280.webp"
                    srcSet="/landing/classroom-800.webp 800w, /landing/classroom-1280.webp 1280w, /landing/classroom-1920.webp 1920w"
                    sizes="(min-width: 1200px) 1136px, 100vw"
                    width={1920}
                    height={1319}
                    alt="An empty high-school science classroom with lab benches, stools and a whiteboard"
                    decoding="async"
                    fetchpriority="high"
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: { xs: 260, sm: 380, md: 560 },
                        objectFit: 'cover',
                        borderRadius: 8,
                        boxShadow: tokens.shadowLift,
                    }}
                />
                <Box
                    sx={{
                        display: 'grid',
                        gap: 2,
                        mt: { xs: 2, md: 0 },
                    }}
                >
                    <Reveal
                        delay={150}
                        sx={{ position: { md: 'absolute' }, left: { md: -32 }, bottom: { md: 40 }, width: { md: 460 } }}
                    >
                        <LabelledExcerptCard />
                    </Reveal>
                    <Reveal
                        delay={300}
                        sx={{ position: { md: 'absolute' }, right: { md: -24 }, bottom: { md: -56 }, width: { md: 300 } }}
                    >
                        <LessonSummaryCard />
                    </Reveal>
                </Box>
            </Box>
        </Container>
    </Box>
);

export default ProductShowcase;
